import { useEffect, useLayoutEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, ArrowLeft, Search, Trash2 } from "lucide-react";
import { api, timeAgo } from "../lib/api";
import ChatThread from "./ChatThread";
import { useVisualViewport, useIsMobile } from "../lib/useVisualViewport";

const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

/** Persistent application-chat inbox shared by the /messages page and both dashboards.
    initialAppId (from ?app=) deep-links straight into one conversation. */
export default function MessagesInbox({ role, testid = "messages", initialAppId = "" }) {
  const [threads, setThreads] = useState(null);
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const openThread = (t) => {
    setOpen(t);
    setThreads((ts) => (ts || []).map((x) => (x.application_id === t.application_id ? { ...x, unread: 0 } : x)));
    if (t.unread > 0) window.dispatchEvent(new CustomEvent("ht:msg-read", { detail: { cleared: t.unread } }));
  };

  // deep link from a notification email: open the exact conversation once threads load
  useEffect(() => {
    if (!initialAppId || open || !threads) return;
    const t = threads.find((x) => x.application_id === initialAppId);
    if (t) openThread(t);
  }, [initialAppId, threads]);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // mobile: an open conversation becomes a fixed, app-like pane pinned to the VISIBLE
  // viewport while the keyboard is up (same pattern as the AI assistant) — the
  // composer stays directly above the keyboard, the message list scrolls itself and
  // the page behind is scroll-locked so nothing jumps or zooms
  const isMobile = useIsMobile();
  const [chatFocus, setChatFocus] = useState(false);
  const vv = useVisualViewport(isMobile && !!open && chatFocus);
  useLayoutEffect(() => {
    if (!(isMobile && open)) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => { html.style.overflow = prev; };
  }, [isMobile, open]);
  useEffect(() => { if (!open) setChatFocus(false); }, [open]);

  // delete is final: it ends the conversation for BOTH sides — the dialog says so plainly
  const deleteThread = (e, t) => {
    e.stopPropagation();
    setDeleteTarget(t);
  };
  const confirmDeleteThread = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    if (!t) return;
    try {
      await api.post(`/messages/threads/${t.application_id}/hide`);
      setThreads((ts) => (ts || []).filter((x) => x.application_id !== t.application_id));
      if (open?.application_id === t.application_id) setOpen(null);
      toast.success("Conversation deleted.");
    } catch {
      toast.error("Could not delete, please try again.");
    }
  };

  const threadName = (t) => (role === "employer" ? t.candidate_name || "Candidate" : t.company_name || t.job_title);
  const q = search.trim().toLowerCase();
  const visible = (threads || []).filter((t) => !q
    || threadName(t).toLowerCase().includes(q)
    || (t.job_title || "").toLowerCase().includes(q)
    || (t.last_message || "").toLowerCase().includes(q));

  return (
    <div className="grid grid-cols-1 min-w-0 lg:grid-cols-[350px_1fr] gap-5 items-start" data-testid={`${testid}-inbox`}>
      <div className={`min-w-0 flex flex-col h-[calc(100dvh-240px)] min-h-[440px] lg:h-[620px] border border-zinc-200 rounded-xl bg-white overflow-hidden shadow-sm ${open ? "hidden lg:flex" : ""}`}>
        <div className="shrink-0 p-3.5 border-b border-zinc-100">
          <div className="relative w-full flex items-center bg-zinc-100 rounded-full px-4 py-2.5 border border-transparent transition-all focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-zinc-200">
            <Search className="h-4 w-4 text-zinc-400 shrink-0" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              data-testid={`${testid}-search`}
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-base sm:text-sm placeholder:text-zinc-500 pl-2.5" />
          </div>
        </div>
        {!threads ? (
          <div className="flex-1 m-4 rounded-lg animate-pulse bg-zinc-50" />
        ) : threads.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-testid={`${testid}-empty`}>
            <MessageSquare className="h-6 w-6 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-zinc-700">No conversations yet</p>
            <p className="text-xs text-zinc-500 mt-1">
              {role === "employer"
                ? "When you message an applicant, the chat appears here and stays available."
                : "Apply for a job and once the employer replies, the chat appears here and stays available."}
            </p>
          </div>
        ) : visible.length === 0 ? (
          <p className="flex-1 flex items-center justify-center p-6 text-sm text-zinc-400 text-center" data-testid={`${testid}-search-empty`}>No conversations match that search.</p>
        ) : (
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" data-testid={`${testid}-list`}>
            {visible.map((t) => {
              const i = threads.indexOf(t);
              return (
                <button key={t.application_id} onClick={() => openThread(t)} data-testid={`${testid}-thread-${i}`}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-colors duration-150 relative group ${
                    open?.application_id === t.application_id ? "bg-blue-50/70" : "hover:bg-zinc-50"}`}>
                  <span className="relative shrink-0">
                    <span className="w-12 h-12 rounded-full bg-zinc-900 text-white inline-flex items-center justify-center text-base font-semibold shadow-sm">
                      {initials(threadName(t))}
                    </span>
                    {t.unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 border-2 border-white rounded-full text-[10px] font-bold text-white inline-flex items-center justify-center"
                        data-testid={`${testid}-unread-${i}`}>{t.unread}</span>
                    )}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex justify-between items-baseline gap-2 mb-0.5">
                      <span className="text-sm font-bold text-zinc-900 truncate">{threadName(t)}</span>
                      <span className="text-xs text-zinc-400 shrink-0">{t.last_at ? timeAgo(t.last_at) : ""}</span>
                    </span>
                    <span className="block text-xs text-zinc-400 truncate">{t.job_title}</span>
                    <span className="block text-sm text-zinc-500 truncate mt-0.5">
                      {t.last_sender === role ? "You: " : ""}{t.last_message}
                    </span>
                  </span>
                  <span role="button" tabIndex={0} data-testid={`${testid}-delete-${i}`}
                    title="Delete conversation"
                    onClick={(e) => deleteThread(e, t)}
                    onKeyDown={(e) => { if (e.key === "Enter") deleteThread(e, t); }}
                    className="absolute right-2 bottom-2 p-1.5 rounded-full text-zinc-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="min-w-0">
        {open ? (
          isMobile ? (
            <div data-testid={`${testid}-open`}
              style={vv ? { top: vv.top, height: vv.height } : undefined}
              className={`fixed inset-x-0 ${vv ? "" : "top-16 bottom-0"} z-[60] bg-white flex flex-col min-w-0`}>
              <div className="shrink-0 flex items-center gap-3 min-w-0 h-12 px-3 border-b border-zinc-100 bg-white/95 backdrop-blur-md">
                <button onClick={() => setOpen(null)} data-testid={`${testid}-back`}
                  className="shrink-0 whitespace-nowrap h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 hover:border-zinc-500 transition-colors duration-200 bg-white">
                  <ArrowLeft className="h-3.5 w-3.5" /> All messages
                </button>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-950 truncate">
                    {role === "employer" ? `${open.candidate_name || "Candidate"} · ${open.job_title}` : `${open.company_name} · ${open.job_title}`}
                  </p>
                </div>
              </div>
              <ChatThread applicationId={open.application_id} viewerRole={role} testid={`${testid}-chat`} fill onComposerFocus={setChatFocus} />
            </div>
          ) : (
          <div data-testid={`${testid}-open`} className="min-w-0">
            <div className="mb-3 flex items-center gap-3 min-w-0 lg:hidden">
              <button onClick={() => setOpen(null)} data-testid={`${testid}-back`}
                className="lg:hidden shrink-0 whitespace-nowrap h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 hover:border-zinc-500 transition-colors duration-200 bg-white">
                <ArrowLeft className="h-3.5 w-3.5" /> All messages
              </button>
              <div className="min-w-0 lg:hidden">
                <p className="text-sm font-semibold text-zinc-950 truncate">
                  {role === "employer" ? `${open.candidate_name || "Candidate"} · ${open.job_title}` : `${open.company_name} · ${open.job_title}`}
                </p>
              </div>
            </div>
            <ChatThread applicationId={open.application_id} viewerRole={role} testid={`${testid}-chat`} />
          </div>
          )
        ) : (
          <div className="hidden lg:flex border border-dashed border-zinc-300 rounded-xl h-[620px] text-center items-center justify-center bg-white/50" data-testid={`${testid}-placeholder`}>
            <p className="text-sm text-zinc-400">Pick a conversation on the left to read and reply.</p>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-zinc-950/50"
          onClick={() => setDeleteTarget(null)} data-testid={`${testid}-delete-dialog`}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg font-bold text-zinc-950">Delete this conversation?</p>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              This ends the chat with <strong>{threadName(deleteTarget)}</strong> for good. Once deleted,
              neither of you will be able to send messages in it again, and it will not come back.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setDeleteTarget(null)} data-testid={`${testid}-delete-cancel`}
                className="h-9 px-4 text-xs font-semibold rounded-md border border-zinc-300 text-zinc-600 hover:border-zinc-500 transition-colors duration-200">
                Keep conversation
              </button>
              <button onClick={confirmDeleteThread} data-testid={`${testid}-delete-confirm`}
                className="h-9 px-4 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors duration-200">
                Delete for good
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
